export interface BaseTimerArgs<T> {
  callback?: (v: T) => void; // Function to call when race is finished
  controller?: AbortController;
  defaultResponse?: T; // return this on timeout
  doThrow?: boolean; // if false use exceptionResponse
  exceptionCallback?: (e: unknown) => void; // call this on exception
  exceptionResponse?: unknown; // return on exception, if response is not set
  hasResponded?: boolean; // When true do nothing on timeout
  response?: T; // return this for exception, or on timeout
  timeout: number; // Timeout before ending the race
}
export interface TimerArgs<T> extends BaseTimerArgs<T> {
  promise: Promise<T>; // Promise we are waiting on
}
export const timerWithDefault = async <T>(args: TimerArgs<T>): Promise<T> => {
  return new Promise((resolve) => {
    const wait = setTimeout(() => {
      clearTimeout(wait);
      if (!args.hasResponded) {
        if (args.controller) {
          args.controller.abort();
        }
        if (args.response) {
          resolve(args.response);
        } else {
          resolve(args.defaultResponse as T);
        }
      }
    }, args.timeout);
  });
};

const racedPromise = async <T>(args: TimerArgs<T>): Promise<T> => {
  const call = (v: T): void => {
    if (args.callback) {
      args.callback(v);
    }
    args.hasResponded = true;
  };
  return await Promise.race([args.promise, timerWithDefault(args)])
    .then((v: T) => {
      call(v);
      return v;
    })
    .catch((e) => {
      call({} as T);
      if (args.exceptionCallback) {
        args.exceptionCallback(e);
      }
      if (args.response) {
        return args.response;
      }
      if (!args.doThrow) {
        return args.exceptionResponse as T;
      }
      throw e;
    });
};

export default racedPromise;
